import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  richHtmlSx,
  tiptapDocToHtml,
  useConfirmService,
  useContextMenuService,
  useImagePreviewService,
  useMentionResolver,
} from '@onesource/components';
import type { TCommentWithAuthor, TCommentWithAuthorBase } from '@onesource/schemas';
import {
  fetchMentionUsersByIds,
  getMediaUrl,
  getSession,
  useDeleteComment,
  useGetAllProfileQuery,
} from '@onesource/services';
import { formatDistanceToNow } from 'date-fns';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { FaPencil } from 'react-icons/fa6';
import { MdBlock, MdOutlineFlag } from 'react-icons/md';
import { useBlockUserConfirm } from 'src/hooks/use-block-user-confirm';

import { CommentInput } from './comment-input';
import { ReactionPicker } from './reaction-picker';
import { ReactorsAvatarStack } from './reactors-avatar-stack';
import { ReportContentDialog } from './report-content-dialog';

interface CommentItemProps {
  comment: TCommentWithAuthor | TCommentWithAuthorBase;
  spaceId: string;
  postId: string;
  onReplyClick?: () => void;
  isReply?: boolean;
}

const MAX_BODY_LENGTH = 200;

export const CommentItem = ({ comment, spaceId, postId, onReplyClick, isReply = false }: CommentItemProps) => {
  const [showFullBody, setShowFullBody] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);

  const theme = useTheme();
  const { setContextMenu } = useContextMenuService();
  const { setConfirm } = useConfirmService();
  const { openPreview } = useImagePreviewService();

  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });
  const deleteComment = useDeleteComment(postId, comment.id);
  const confirmBlockUser = useBlockUserConfirm();

  const isAuthor = profile?.authUserId === comment.author.id;
  // Report / block only once we know who the viewer is — never on their own comment.
  const canReportOrBlock = !!profile && !isAuthor;

  const handleEditClose = () => {
    setIsEditing(false);
  };

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleDelete = useCallback(() => {
    setConfirm({
      title: 'Delete',
      open: true,
      confirmBtnLabel: 'Remove',
      cancelBtnLabel: 'Cancel',
      message: 'This will permanently remove your comment. Are you sure you want to continue?',
      onConfirm: async () => {
        await deleteComment.mutateAsync();
      },
    });
  }, [setConfirm, deleteComment]);

  const handleReport = useCallback(() => {
    setIsReportOpen(true);
  }, []);

  const handleBlockAuthor = useCallback(() => {
    confirmBlockUser({ authUserId: comment.author.id }, comment.author.name);
  }, [confirmBlockUser, comment.author.id, comment.author.name]);

  const handleMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setContextMenu({
        anchorEl: event.currentTarget,
        items: isAuthor
          ? [
              {
                id: 'edit',
                label: 'Edit',
                icon: <FaPencil color={theme.palette.primary.main} size={16} />,
                color: 'info',
                context: comment,
                onClick: handleEdit,
              },
              {
                id: 'delete',
                label: 'Delete',
                icon: <FaRegTrashAlt color={theme.palette.primary.main} size={16} />,
                color: 'info',
                context: comment,
                onClick: handleDelete,
              },
            ]
          : canReportOrBlock
            ? [
                {
                  id: 'report',
                  label: 'Report',
                  icon: <MdOutlineFlag size={18} />,
                  color: 'error',
                  context: comment,
                  onClick: handleReport,
                },
                {
                  id: 'block',
                  label: 'Block user',
                  icon: <MdBlock size={18} />,
                  color: 'error',
                  context: comment,
                  onClick: handleBlockAuthor,
                },
              ]
            : [],
      });
    },
    [
      setContextMenu,
      theme.palette.primary.main,
      comment,
      isAuthor,
      canReportOrBlock,
      handleEdit,
      handleDelete,
      handleReport,
      handleBlockAuthor,
    ],
  );

  const bodyDoc = useMemo(() => {
    try {
      return JSON.parse(comment.body);
    } catch {
      return null;
    }
  }, [comment.body]);
  const resolvedDoc = useMentionResolver(bodyDoc, fetchMentionUsersByIds);

  const [resolvedBodyHtml, setResolvedBodyHtml] = useState<string>('');

  useEffect(() => {
    if (resolvedDoc) {
      setResolvedBodyHtml(DOMPurify.sanitize(tiptapDocToHtml(resolvedDoc)));
      return;
    }
    const rawHtml = comment.bodyHtml ?? comment.body;
    const uuidRegex = /@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
    const uuids = [...new Set([...rawHtml.matchAll(uuidRegex)].map(m => m[1]))];
    if (uuids.length === 0) {
      setResolvedBodyHtml(DOMPurify.sanitize(rawHtml));
      return;
    }
    fetchMentionUsersByIds(uuids).then(users => {
      let resolved = rawHtml;
      for (const user of users) {
        resolved = resolved.replaceAll(`@${user.id}`, `<span class="mention">@${user.displayName}</span>`);
      }
      setResolvedBodyHtml(DOMPurify.sanitize(resolved));
    });
  }, [resolvedDoc, comment.bodyHtml, comment.body]);

  const bodyHtml = resolvedBodyHtml || DOMPurify.sanitize(comment.bodyHtml ?? comment.body);

  const shouldTruncate = comment.body.length > MAX_BODY_LENGTH;

  const timeAgo = formatDistanceToNow(new Date(comment.createdAt), { addSuffix: false });

  const replies = 'replies' in comment ? comment.replies : [];
  const replyCount = 'replyCount' in comment ? comment.replyCount : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '8px', md: '12px' },
        pl: isReply ? '40px' : 0,
      }}
    >
      {/* Comment Row */}
      <Box sx={{ display: 'flex', gap: { xs: '8px', md: '12px' }, alignItems: 'flex-start' }}>
        <Avatar
          src={getMediaUrl(comment.author.avatar)}
          alt={comment.author.name}
          sx={{ width: 30, height: 30, borderRadius: '15px', flexShrink: 0 }}
        />

        {/* Comment Bubble */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#eee',
            borderRadius: '12px',
            px: '17px',
            py: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '2px', md: '6px' },
          }}
        >
          {/* Author Name + menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography
              sx={{
                flex: 1,
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 700,
                fontSize: { xs: 14, md: 14 },
                lineHeight: '19px',
                color: '#333',
              }}
            >
              {comment.author.name}
            </Typography>
            <IconButton size="small" sx={{ p: 0 }} onClick={isEditing ? handleEditClose : handleMenuClick}>
              {isEditing ? (
                <CloseIcon sx={{ fontSize: 18, color: '#333' }} />
              ) : (
                <MoreHorizIcon sx={{ fontSize: 18, color: '#333' }} />
              )}
            </IconButton>
          </Box>

          {/* Comment Body */}
          {isEditing ? (
            <CommentInput
              spaceId={spaceId}
              postId={postId}
              commentId={comment.id}
              initialBody={bodyDoc}
              onClose={handleEditClose}
            />
          ) : (
            <Box
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
              sx={{
                ...richHtmlSx,
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 400,
                fontSize: { xs: 12, md: 12 },
                wordBreak: 'break-word',
                lineHeight: '18px',
                color: '#333',
                ...(!showFullBody &&
                  shouldTruncate && {
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }),
                '& span.mention': {
                  color: theme => theme.palette.primary.main,
                  fontWeight: 600,
                  backgroundColor: theme => `${theme.palette.primary.main}22`,
                  borderRadius: '4px',
                  px: '4px',
                  py: '2px',
                },
              }}
            />
          )}

          {/* See More */}
          {shouldTruncate && !showFullBody && (
            <Typography
              onClick={() => setShowFullBody(true)}
              sx={{
                alignSelf: 'flex-start',
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 700,
                fontSize: 12,
                lineHeight: '17px',
                color: '#333',
                cursor: 'pointer',
              }}
            >
              See more
            </Typography>
          )}

          {/* Image Attachments */}
          {comment.attachments && comment.attachments.length > 0 && (
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '4px' }}>
              {comment.attachments.map((attachment, index) => {
                // Stored URLs may be relative paths (e.g. a compressed
                // derivative); getMediaUrl prefixes the CDN base for those.
                const src = getMediaUrl(attachment.url) ?? attachment.url;
                return (
                  <Box
                    key={attachment.url}
                    component="img"
                    src={src}
                    alt={`Attachment ${index + 1}`}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 240,
                      borderRadius: '12px',
                      objectFit: 'contain',
                      cursor: 'pointer',
                    }}
                    onClick={() => openPreview(src, `Attachment ${index + 1}`)}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Comment Actions Row */}
      <Box sx={{ display: 'flex', gap: '11px', alignItems: 'center', pl: '42px' }}>
        <Typography
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontWeight: 400,
            fontSize: { xs: 12, md: 12 },
            lineHeight: '17px',
            color: '#808080',
          }}
        >
          {timeAgo}
        </Typography>

        {/* No `onReplyClick` means the viewer can't comment here (an admin
            restricted `comment.create` for this community) — keep the reply
            count visible, drop the affordance. */}
        {!isReply && (onReplyClick || !!replyCount) && (
          <Box
            sx={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: onReplyClick ? 'pointer' : 'default' }}
            onClick={onReplyClick}
          >
            <Typography
              sx={{
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 400,
                fontSize: { xs: 12, md: 12 },
                lineHeight: '17px',
                color: '#333',
              }}
            >
              {onReplyClick
                ? `Reply ${replyCount && replyCount > 0 ? replyCount : ''}`.trim()
                : `${replyCount} replies`}
            </Typography>
          </Box>
        )}

        <ReactionPicker
          targetType="comment"
          targetId={comment.id}
          currentReaction={'userReaction' in comment ? comment.userReaction : null}
          likeCount={comment.likeCount}
        />

        <ReactorsAvatarStack targetType="comment" targetId={comment.id} likeCount={comment.likeCount} />
      </Box>

      {!isAuthor && (
        <ReportContentDialog
          open={isReportOpen}
          onClose={() => setIsReportOpen(false)}
          targetType="comment"
          targetId={comment.id}
        />
      )}

      {/* Nested Replies */}
      {replies && replies.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          {/* Vertical connecting line */}
          <Box
            sx={{
              position: 'absolute',
              left: '30px',
              top: 0,
              bottom: '50%',
              width: '2px',
              bgcolor: '#ddd',
              borderRadius: '1px',
            }}
          />
          {replies.map((reply: TCommentWithAuthorBase) => (
            <CommentItem key={reply.id} comment={reply} spaceId={spaceId} postId={postId} isReply />
          ))}
        </Box>
      )}
    </Box>
  );
};
